import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { GridOptions } from 'ag-grid-community';
import * as moment from 'moment';
import { toLower } from 'lodash';

// Componentes y Renders
import { CeqTableComponent } from '../../common/ceq-table/ceq-table.component';
import { AttachmentChildRendered } from '../../common/renders/attachment-child-render';
import { DeleteChildRendered } from '../../common/renders/delete-child-rendered';
import { ViewChildRendered } from '../../common/renders/view-child-rendered';

// Modelos y Servicios
import { Attachment } from '../../common/models/Attachment.Model';
import { ProxyGet } from '../../common/services/proxy.service';
import { PurchaseService } from '../purchase.service';
import { GlobalFunctionsService } from '../../common/functions/global-functions.service';
import { SettingsService } from 'src/app/core/settings/settings.service';
import { SingleResponseJson } from '../../common/models/response';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-attachment',
  templateUrl: './attachment.component.html',
  styleUrls: ['./attachment.component.scss']
})
export class AttachmentComponent implements OnInit {
  @ViewChild('CeqGrid', { static: false }) ceqGrid: CeqTableComponent;
  @ViewChild('waveLoad') myInputVariable: ElementRef;

  // Parámetros
  table: string;
  key: string;
  subtitulo: string;
  permisos: string;

  // Flags de Estado
  Working = false;
  saving = false;
  canEliminate = false;
  canCreate = false;
  canDownload = false;
  
  // Previsualización
  showIframe = false;
  isImage = false;
  isText = false;
  title: string;
  img: SafeUrl;
  contentString: string;
  sanitizedUrl: SafeResourceUrl; // Específico para el iframe
  
  // Archivos y Configuración
  attachmentList: Attachment[] = [];
  uploadedWave: File[] = [];
  fileSize = 1024000; 
  DefaultSize = 5120000;

  gridOptions: GridOptions = {
    context: this,
    frameworkComponents: {
      childViewMessageRenderer: ViewChildRendered,
      childDeleteMessageRenderer: DeleteChildRendered,
      childAttachment: AttachmentChildRendered
    },
    icons: {
      sortAscending: '<i class="fa fa-sort-up" style="margin-left:10px"></i>',
      sortDescending: '<i class="fa fa-sort-down" style="margin-left:10px"></i>',
      sortUnSort: '<i class="fa fa-sort" style="margin-left:10px"></i>'
    },
    suppressHorizontalScroll: true,
    onRowDoubleClicked(event: any) { }
  };

  columnDefs = [
    { headerName: 'Archivo', field: 'attachedFileName', width: 50, sortable: true, unSortIcon: true, resizable: true },
    { 
      headerName: 'Subido en', 
      field: 'CreatedAt', 
      width: 20, 
      sortable: true, 
      unSortIcon: true,
      cellRenderer: (params: any) => moment(params.data.ModifiedAt).format('YYYY-MM-DD HH:mm:ss')
    },
    { headerName: 'Subido por', field: 'CreatedBy', width: 20, resizable: true },
    { headerName: 'Vista Previa', field: 'icon', width: 10, cellRenderer: 'childViewMessageRenderer', colId: 'params', cellClass: 'text-center' },
    { headerName: 'Descargar', field: 'icon', width: 10, cellRenderer: 'childAttachment', colId: 'params', cellClass: 'text-center' },
    { headerName: 'Eliminar', field: 'icon', width: 10, cellRenderer: 'childDeleteMessageRenderer', colId: 'params', cellClass: 'text-center' }
  ];

  constructor(
    private apiService: ProxyGet,
    private businessService: PurchaseService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private globalService: GlobalFunctionsService,
    private settingsServ: SettingsService
  ) { }

  ngOnInit(): void {
    this.loadInitialData();
  }

  private loadInitialData() {
    this.table = this.route.snapshot.params['Table'];
    this.key = this.route.snapshot.params['Key'];
    this.subtitulo = this.route.snapshot.params['Title'];
    this.permisos = this.route.snapshot.params['Perm'] || '';

    if (!this.table || !this.key) {
      this.globalService.messageToast('Faltan identificadores de tabla o llave', 'warning');
      return;
    }

    // Configuración de Permisos
    this.canCreate = this.permisos.toUpperCase().includes('C');
    this.canDownload = this.permisos.toUpperCase().includes('D');
    this.canEliminate = this.permisos.toUpperCase().includes('E');

    // Carga de configuración de tamaño
    this.settingsServ.getSettingsByKey('UploadSize').subscribe({
      next: (conf: any) => {
        this.fileSize = Number(conf.Value);
        this.DefaultSize = Number(conf.Value);
      },
      error: () => {}
    });

    this.getAttachments();
  }

  getAttachments() {
    this.businessService.getAttachmentList(this.table, this.key).subscribe((data: Attachment[]) => {
      this.attachmentList = data;
      if (this.ceqGrid) {
        this.ceqGrid.data = data;
      }
    });
  }

  // --- MÉTODOS LLAMADOS POR LOS RENDERERS ---

  async view(item: any) {
    await this.globalService.messageBimbo('Por favor espere...', 'loading', 'Cargando datos...');
    const fileName = item.data.attachedFileName;
    const array = fileName.split('.');
    const extension = toLower(array[array.length - 1]);
    
    this.resetPreview();

    if (['eml', 'msg'].includes(extension)) {
      Swal.close();
      this.globalService.messageToast(`Los archivos .${extension} deben descargarse para ser vistos.`, 'warning');
      return;
    }

    // Imágenes
    if (['jpg', 'jpeg', 'gif', 'png', 'bmp'].includes(extension)) {
      this.businessService.viewAttachmenRecord(item.data.attachedId).subscribe((resp: any) => {
        Swal.close();
        if (!resp) return;
        this.title = fileName;
        const objectURL = URL.createObjectURL(new Blob([resp], { type: resp.type }));
        this.img = this.sanitizer.bypassSecurityTrustUrl(objectURL);
        this.isImage = true;
      });
    } 
    // Texto / XML
    else if (['xml', 'txt'].includes(extension)) {
      this.businessService.viewAttachmenRecord(item.data.attachedId).subscribe(async (resp: any) => {
        Swal.close();
        if (!resp) return;
        this.title = fileName;
        this.contentString = await resp.text();
        this.isText = true;
      });
    } 
    // PDF y Otros (Uso de Iframe con Sanitización de Recurso)
    else {
      const jsonDta = await this.apiService.callAPI(`attachment/viewSave/${item.data.attachedId}/${extension}`, "GET");
      Swal.close();
      const response = jsonDta as SingleResponseJson<string>;
      if (response && response.Success) {
        this.title = fileName;
        this.showIframe = true;
        // FIX: Se usa bypassSecurityTrustResourceUrl para evitar el error unsafe value en IFRAME
        const pdfUrl = `data:application/pdf;base64,${response.Response}`;
        this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
      } else {
        this.globalService.messageToast('Error al obtener vista previa', 'warning');
      }
    }
  }

  edit(item: any): void {
    if (!this.canDownload) {
      this.globalService.messageToast('No cuenta con permisos para descargar', 'error');
      return;
    }
    
    this.businessService.viewAttachmenRecord(item.data.attachedId).subscribe((resp: any) => {
      if (!resp) return;
      const blob = new Blob([resp], { type: resp.type });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.download = item.data.attachedFileName;
      anchor.href = url;
      anchor.click();
      window.URL.revokeObjectURL(url);
    });
  }

  async delete(item: any): Promise<void> {
    if (!this.canEliminate) {
      this.globalService.messageToast('No cuenta con permisos para realizar esta acción', 'error');
      return;
    }

    const result = await this.globalService.messageBimbo(
      'El registro no podrá ser recuperado.', 
      'question1', 
      `¿Desea eliminar el archivo adjunto ${item.data.attachedFileName}?`
    );

    if (result) {
      await this.globalService.messageBimbo('Por favor espere...', 'loading', 'Cargando datos...');
      this.businessService.removeAttachmenRecord(item.data.attachedId).subscribe({
        next: () => {
          Swal.hideLoading();
          this.globalService.messageToast('Archivo eliminado con éxito', 'success');
          this.getAttachments();
        },
        error: (err) => {
          Swal.hideLoading();
          this.globalService.messageToast('No se pudo eliminar el archivo', 'error');
          console.error(err);
        }
      });
    }
  }

  // --- CARGA DE ARCHIVOS ---

  async onWaveLoadSelect(data: any) {
    if (!data || !data[0] || this.Working) return;
    
    const file = data[0];
    const nameFile = file.name;

    if (nameFile.length > 100) {
      this.globalService.messageToast("El nombre del archivo es demasiado largo (máx 100).", 'warning');
      return;
    }

    // Validar si ya existe
    if (this.attachmentList && this.attachmentList.find(a => a.attachedFileName === nameFile)) {
      this.globalService.messageToast("El nombre del archivo ya existe.", 'warning');
      this.myInputVariable.nativeElement.value = '';
      return;
    }

    // Validar extensión
    const ext = toLower(nameFile.split('.').pop());
    const validExts = ['xls', 'xlsx', 'pdf', 'doc', 'docx', 'jpg', 'jpeg', 'bmp', 'gif', 'png', 'xml', 'txt', 'msg', 'eml'];
    
    if (!validExts.includes(ext)) {
      this.globalService.messageToast("Formato de archivo no permitido.", 'warning');
      this.myInputVariable.nativeElement.value = '';
      return;
    }

    if (file.size > this.DefaultSize) {
      this.globalService.messageToast("El archivo supera el tamaño permitido.", 'warning');
      this.myInputVariable.nativeElement.value = '';
      return;
    }

    this.Working = true;
    this.uploadedWave = [file];
    await this.onSendWaveLoad();
  }

  async onSendWaveLoad() {
    await this.globalService.messageBimbo('Por favor espere...', 'loading', 'Guardando datos...');
    const dataForm = new FormData();
    dataForm.append('file-0', this.uploadedWave[0]);

    this.saving = true;
    this.businessService.SendFile(dataForm, this.table, this.key).subscribe({
      next: () => {
        Swal.hideLoading();
        this.globalService.messageToast('Archivo adjunto correctamente', 'success');
        this.getAttachments();
        this.resetUploadState();
      },
      error: (err) => {
        Swal.hideLoading();
        this.globalService.messageToast('Error al cargar el archivo', 'error');
        this.resetUploadState();
        console.error(err);
      }
    });
  }

  private resetUploadState() {
    this.Working = false;
    this.saving = false;
    this.uploadedWave = [];
    if (this.myInputVariable) this.myInputVariable.nativeElement.value = '';
  }

  private resetPreview() {
    this.isImage = false;
    this.isText = false;
    this.showIframe = false;
    this.sanitizedUrl = null;
  }

  ListEmiter(gridOptions: GridOptions) {
    this.gridOptions = gridOptions;
  }

  btnCancelOnClick() {
    window.close();
  }
}